export interface BannerPriceLabels {
  /** Pełny podpis z konfiguracji / tłumaczenia, np. "Ceny od", "Prices from". */
  pricesFrom: string;
  /** Krótki podpis, np. "Od", "From". */
  from: string;
  /** Jednostka waluty z tłumaczenia, np. "zł" albo "PLN". */
  currency: string;
}

export type BannerPrice =
  | { type: "empty" }
  | { type: "text"; text: string }
  | { type: "price"; prefix: string; amount: string; suffix: string };

/**
 * `ł` nie jest znakiem słowa w JS, więc \b po "zł" nie działa.
 * Wielkość liter (ZŁ, zl) normalizujemy przed dopasowaniem.
 */
const currencyPattern = () => /(\d+(?:[.,]\d+)?)\s*(zł|PLN)(?![a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ])/g;
const numberPattern = () => /\d+(?:[.,]\d+)?/g;

const collectMatches = (text: string, pattern: RegExp): RegExpExecArray[] => {
  const matches: RegExpExecArray[] = [];
  let match = pattern.exec(text);
  while (match) {
    matches.push(match);
    match = pattern.exec(text);
  }
  return matches;
};
const LEFTOVER_CURRENCY_RE = /^(?:zł|PLN)(?![a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ])/;

const preparePriceText = (text: string) => text.replace(/z[lłŁ]/gi, "zł").replace(/pln/gi, "PLN");

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const normalizeCurrency = (token: string | undefined, fallback: string): string => {
  const source = (token || fallback || "zł").trim();
  if (/^(zł|zl)$/i.test(source)) return "zł";
  if (/^pln$/i.test(source)) return "PLN";
  return source;
};

const formatAmount = (rawNumber: string, currencyToken: string | undefined, fallbackCurrency: string): string => {
  const currency = normalizeCurrency(currencyToken, fallbackCurrency);
  const usesComma = rawNumber.includes(",");
  const numeric = Number(rawNumber.replace(/\s/g, "").replace(",", "."));

  if (!Number.isFinite(numeric)) {
    return `${rawNumber} ${currency}`;
  }

  const rounded = Math.round(numeric * 100) / 100;
  const isWhole = Math.abs(rounded - Math.round(rounded)) < 0.001;
  const display = isWhole
    ? String(Math.round(rounded))
    : (usesComma ? rounded.toFixed(2).replace(".", ",") : rounded.toFixed(2));

  return `${display} ${currency}`;
};

/**
 * "Ceny od" (i odpowiedniki z tłumaczeń) skracamy do "od".
 * Samo "Od" pokazujemy małymi literami, jak na banerze.
 */
const shortenPrefix = (prefix: string, labels: BannerPriceLabels): string => {
  const cleaned = prefix.replace(/[:\-–—]+$/g, "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "";

  const fromLabel = labels.from.trim().toLocaleLowerCase();
  const pricesFrom = labels.pricesFrom.trim();

  if (pricesFrom && new RegExp(`^${escapeRegExp(pricesFrom)}$`, "i").test(cleaned)) {
    return fromLabel;
  }

  if (/^ceny\s+od$/i.test(cleaned)) {
    return "od";
  }

  if (pricesFrom && new RegExp(escapeRegExp(pricesFrom), "i").test(cleaned)) {
    return cleaned.replace(new RegExp(escapeRegExp(pricesFrom), "i"), fromLabel).replace(/\s+/g, " ").trim();
  }

  if (/ceny\s+od/i.test(cleaned)) {
    return cleaned.replace(/ceny\s+od/i, "od").replace(/\s+/g, " ").trim();
  }

  if (/^od$/i.test(cleaned) || (labels.from.trim() && new RegExp(`^${escapeRegExp(labels.from.trim())}$`, "i").test(cleaned))) {
    return fromLabel;
  }

  return cleaned;
};

const stripLeftoverCurrency = (suffix: string): string => {
  let value = preparePriceText(suffix).trim();
  while (LEFTOVER_CURRENCY_RE.test(value)) {
    value = value.replace(LEFTOVER_CURRENCY_RE, "").trim();
  }
  return value.replace(/\s+/g, " ").trim();
};

const STATUS_PRICE = /^(?:ładowanie\.\.\.|brak biletów)$/i;

/**
 * Wstawia kwotę w tekst z konfiguracji stepSale.
 * Sam podpis bez {{price}} i bez liczby nie może zjeść kwoty — doklejamy ją.
 */
export const applyMainPriceTemplate = (template: string | number | undefined | null, price: string | number): string => {
  const rawTemplate = String(template ?? "").trim();
  const rawPrice = String(price ?? "").trim();
  if (!rawTemplate) return rawPrice;

  const withPrice = rawTemplate.replace(/\{\{\s*price\s*\}\}/gi, rawPrice);
  if (withPrice !== rawTemplate) {
    return withPrice;
  }

  if (/\d/.test(rawTemplate)) return rawTemplate;
  if (STATUS_PRICE.test(rawPrice) || !rawPrice) return rawPrice || rawTemplate;
  return `${rawTemplate} ${rawPrice}`;
};

/**
 * Rozbija cenę z banera (liczba albo tekst z stepSale, np. "Ceny od {{price}} zł")
 * na podpis, kwotę i resztę. Kwota zawsze w postaci "34 zł".
 * preserveCaption zostawia wpisany podpis (np. "Ceny od") zamiast skracać go do "od".
 */
export const parseBannerPrice = (
  raw: string | number,
  labels: BannerPriceLabels,
  options?: { preserveCaption?: boolean }
): BannerPrice => {
  const text = preparePriceText(String(raw ?? "").trim());
  if (!text) return { type: "empty" };

  const currencyMatches = collectMatches(text, currencyPattern());
  const numberMatches = collectMatches(text, numberPattern());
  const match = currencyMatches.length ? currencyMatches[currencyMatches.length - 1] : numberMatches[numberMatches.length - 1];

  if (!match || match.index === undefined) {
    return { type: "text", text };
  }

  const rawNumber = match[1] ?? match[0];
  const currencyToken = match[1] ? match[2] : undefined;
  const start = match.index;
  const end = start + match[0].length;

  const rawPrefix = text.slice(0, start);
  const prefix = options?.preserveCaption
    ? rawPrefix.replace(/[:\-–—]+$/g, "").replace(/\s+/g, " ").trim()
    : shortenPrefix(rawPrefix, labels);
  const suffix = stripLeftoverCurrency(text.slice(end));

  return {
    type: "price",
    prefix,
    amount: formatAmount(rawNumber, currencyToken, labels.currency),
    suffix,
  };
};
